import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Chip, Spinner } from '@heroui/react';
import { Plus } from 'lucide-react';
import { useTemplatesPage } from './useTemplatesPage';
import { TEMPLATE_STATUS_COLORS, TEMPLATE_STATUS_LABELS } from '@/lib/templateStatus';
import { PageContainer } from '@/components/templates/PageContainer';

export function TemplatesPage() {
  const { templates, isLoading, error, isCreating, createTemplate } = useTemplatesPage();
  const navigate = useNavigate();

  // Создание — сразу на бэке: шаблон появляется в статусе new (в списке не
  // виден), страница /templates/:id открывается в режиме редактирования
  const handleCreate = async () => {
    const created = await createTemplate({});
    if (created) navigate(`/templates/${created.id}`);
  };

  return (
    <PageContainer>
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-foreground">Шаблоны</h2>
        <Button
          variant="primary"
          className="shrink-0"
          onPress={() => void handleCreate()}
          isDisabled={isCreating}
        >
          {isCreating ? <Spinner size="sm" /> : <Plus className="size-4" />}
          Создать шаблон
        </Button>
      </div>

      {error && (
        <p className="mb-3 rounded-[var(--radius)] bg-danger px-3 py-2 text-sm text-danger-foreground">
          {error}
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted">
          <Spinner size="sm" />
          Загрузка…
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-surface">
          <div className="p-6 text-center text-sm text-muted">
            Шаблоны отсутствуют. Создайте первый шаблон.
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            // Вся карточка — ссылка на страницу шаблона
            <Link key={t.id} to={`/templates/${t.id}`} className="group">
              <Card className="bg-surface transition-colors group-hover:border-accent">
                <div className="flex min-w-0 flex-col gap-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2">
                      {/* Цветная точка — если задан акцентный цвет шаблона */}
                      {t.color && /^#[0-9a-fA-F]{6}$/.test(t.color) && (
                        <span
                          className="mt-1.5 size-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: t.color }}
                          aria-label={t.color}
                        />
                      )}
                      {/* min-h-12 — резерв под 2 строки, чтобы карточки были одной высоты */}
                      <h3 className="line-clamp-2 min-h-12 break-words text-base font-semibold text-foreground">
                        {t.title}
                      </h3>
                    </div>
                    <Chip size="sm" variant="soft" color="accent">
                      {t.cost ?? '0'} ₽
                    </Chip>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip
                      size="sm"
                      variant="soft"
                      color={TEMPLATE_STATUS_COLORS[t.status] ?? 'default'}
                    >
                      {TEMPLATE_STATUS_LABELS[t.status] ?? t.status}
                    </Chip>
                  </div>
                  {/* Резервируем 2 строки под описание всегда (min-h-10 = 2 × 1.25rem
                      line-height text-sm) — иначе карточки разной высоты */}
                  <p className="line-clamp-2 min-h-10 break-words text-sm text-muted">
                    {t.description}
                  </p>
                  <span className="truncate text-xs text-muted">{t.slug}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
